'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { useWallet } from '@/hooks/use-wallet';

/** Shared GET `/api/users/[address]` — use for React Query prefetch (no cache-busting query string). */
export async function fetchUserProfileJson(
  address: string,
): Promise<UserProfileData | null> {
  const res = await fetch(`/api/users/${encodeURIComponent(address.toLowerCase())}`);
  if (!res.ok) return null;
  const json = await res.json();
  return json.data as UserProfileData;
}

export interface UserProfileData {
  address: string;
  displayName: string | null;
  avatarUrl: string | null;
  source: 'ens' | 'farcaster' | 'twitter' | 'generated' | 'custom' | null;
  level?: number; // Preregistration flair level (0-6)
  customAvatar?: string; // Custom uploaded avatar URL
  twitter?: {
    username: string;
    avatar?: string;
  };
  farcaster?: {
    username: string;
    avatar?: string;
  };
  ens?: {
    name: string;
    avatar?: string;
  };
}

/**
 * Hook to fetch and cache user profile data from the API.
 * 
 * This returns the correct avatar URL respecting the priority:
 * custom_avatar > farcaster_avatar > twitter_avatar > ens_avatar > generated
 * 
 * Use this instead of wallet.avatarUrl to ensure custom avatars are displayed.
 */
export function useUserProfile(address: string | null | undefined) {
  const queryClient = useQueryClient();
  const { user } = useWallet();
  const twitterConnected = !!user?.twitter?.username;

  const query = useQuery({
    queryKey: ['user-profile', address?.toLowerCase()],
    queryFn: async (): Promise<UserProfileData | null> => {
      if (!address) return null;
      return fetchUserProfileJson(address);
    },
    enabled: !!address,
    staleTime: twitterConnected ? 30 * 1000 : Infinity,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: twitterConnected,
    refetchOnWindowFocus: twitterConnected,
    refetchOnReconnect: twitterConnected,
  });

  // Helper to invalidate profile cache and force immediate refetch
  const invalidateProfile = useCallback(async () => {
    if (address) {
      // Clear the cache and force immediate refetch
      await queryClient.invalidateQueries({ 
        queryKey: ['user-profile', address.toLowerCase()],
        refetchType: 'all',
      });
    }
  }, [address, queryClient]);

  // Compute effective avatar URL with fallback — only use profile data
  // when it actually matches the current address. Otherwise we're still
  // rendering the previous wallet's avatar via TanStack's placeholder.
  const profileMatchesAddress =
    !!query.data?.address &&
    !!address &&
    query.data.address.toLowerCase() === address.toLowerCase();

  const effectiveAvatarUrl = profileMatchesAddress
    ? query.data?.avatarUrl || (address ? generateGridAvatarUrl(address, 256) : null)
    : (address ? generateGridAvatarUrl(address, 256) : null);

  // `isProfileLoading` (always-on): true whenever the profile we're
  // holding in cache doesn't match the current address — i.e. the
  // observer is either fetching initial data or still showing stale
  // placeholder data from the previous wallet. Unlike the gated
  // `isLoading` below, this flips true on wallet switch regardless of
  // whether X is linked, so the portfolio / legacy wallet dropdowns can
  // render a skeleton while the new wallet's profile loads in.
  const isProfileLoading = !!address && (query.isLoading || !profileMatchesAddress);

  return {
    profile: query.data,
    avatarUrl: effectiveAvatarUrl,
    displayName: profileMatchesAddress ? query.data?.displayName : undefined,
    level: profileMatchesAddress ? (query.data?.level ?? 0) : 0,
    /**
     * Ungated loading flag. True whenever the cached profile doesn't
     * belong to the current address — use to drive avatar / displayName
     * skeletons across wallet switches. Prefer this over `isLoading`
     * (kept for back-compat with the legacy non-X-link heuristic).
     */
    isProfileLoading,
    /** Legacy: omit loading UI for avatar when X is not linked — we show generative immediately. */
    isLoading: twitterConnected ? query.isLoading : false,
    isFetching: twitterConnected ? query.isFetching : false,
    isError: query.isError,
    invalidateProfile,
    refetch: query.refetch,
  };
}
